import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Formik } from 'formik';
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  Grid,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  InputAdornment,
  Autocomplete,
  Link
} from '@mui/material';
import {
  CalendarToday,
  AccessTime,
  Search,
  Close
} from '@mui/icons-material';
import { DatePicker, TimePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';

import 'dayjs/locale/en-gb';
import { getSalesperson } from '../../service/User/getSalesperson';
import { getRegions } from '../../service/Address/getRegions';
import { qaddressSearch } from '../../service/Address/searchaddress';
import { getOfficeDetails } from '../../service/Office/getOfficeDetails';
import { getpropertySubType } from '../../service/Listings/getpropertyType';
import { getStates } from '../../service/Address/getStates';
import { getSuburbs } from '../../service/Address/getSuburbs';
import { editListing } from '../../service/Listings/editListings';
import { getListing } from '../../service/Listings/getListings';
import { useDebouncedCallback } from 'use-debounce';
import { parseTimeStringToDate, formatDate, formatTime } from '../../service/Util/dateTimeUtil';
import * as Yup from 'yup';

const validationSchema = Yup.object({
  streetNumber: Yup.string().required('Street number is required'),
  streetName: Yup.string().required('Street name is required'),
  regionid: Yup.string().required('Region is required'),
  userid: Yup.string().required('Salesperson is required'),
  propertySaleTypeid: Yup.string().required('Sale type is required'),
  listingDate: Yup.date().required('Listing date is required'),
});





const SaleType = [
  { label: 'For Sale', value: '1' },
  { label: 'Auction', value: '2' },
  { label: 'Tender', value: '3' }
];

const rentalType = [
  { label: 'Lease', value: '4' }
];

const landSizeUnits = [
  { label: 'm²', value: 'squareMetre' },
  { label: 'ft²', value: 'squareFoot' },
  { label: 'Hectares', value: 'hectare' },
  { label: 'Acres', value: 'acre' }
];

const rentalListingStatus = [
  { label: 'Current', value: '1' },
  { label: 'Leased', value: '6' },
  { label: 'Withdrawn/Deleted', value: '5' },
];

const saleListingStatus = [
  { label: 'Archived Sale', value: '3' },
  { label: 'Current', value: '1' },
  { label: 'Recent Sales', value: '4' },
  { label: 'Under Contract/Offer', value: '2' },
  { label: 'Withdrawn/Deleted', value: '5' }
];

const commercialRateType = [
  { label: 'Per Week', value: '300' },
  { label: 'Per Month', value: '301' },
  { label: 'Per Annum', value: '302' }
];

const leaseRateType = [
  { label: 'Per Week', value: '200' },
  { label: 'Per Month', value: '201' },
];

const holidayRateType = [
  { label: 'Per Week', value: '100' },
  { label: 'Per Month', value: '101' },
];


const ListingFormEdit = () => {
  const { propertyId } = useParams();
  const [isLoading, setIsLoading] = useState(true);
  const [Salesperson, setSalesperson] = useState([]);
  const [Regions, setRegions] = useState([]);
  const [OfficeDetails, setOfficeDetails] = useState([]);
  const [listing, setlisting] = useState([]);
  const [states, setStates] = useState([]);
  const [suburbs, setSuburbs] = useState([]);
  const [propertySubTypes, setPropertySubTypes] = useState([]);
  const [isSavingListing,setIsSavingListing]= useState(false);

  const fetchAllData = async () => {
  setIsLoading(true);
  try {
    const [
      salespersonResponse,
      regionsResponse,
      officeDetailsResponse,
      selectListingResponse,
      stateResponse
    ] = await Promise.all([
      getSalesperson("listing_view"),
      getRegions(),
      getOfficeDetails(),
      getListing(propertyId),
      getStates()
    ]);

    setSalesperson(salespersonResponse.data);
    setRegions(regionsResponse.data);
    setOfficeDetails(officeDetailsResponse.data);
    setlisting(selectListingResponse.data);
    setStates(stateResponse.data);

   
    if (selectListingResponse.data && selectListingResponse.data.stateId && selectListingResponse.data.state) {
      const suburbResponse = await getSuburbs(selectListingResponse.data.stateId, selectListingResponse.data.state);
      setSuburbs(suburbResponse.data);
    }

  } catch (error) {
  } finally {
    setIsLoading(false);
  }
};


useEffect(() => {
  fetchAllData();
}, []);



 

   useEffect(() => {
      const fetchSubTypes = async () => {
        if (!listing?.propertyType) return;
        try {
          const response = await getpropertySubType(listing.propertyType);
          setPropertySubTypes(response.data);
        } catch (error) {
          AlertUtils.showError('Error','Error fetching property subtypes, please try again.');
        }
      };

      fetchSubTypes();
    }, [listing?.propertyType]);


    const searchAddress = useDebouncedCallback(async (searchAddressText) => {
      setIsSearchingAddress(true);
      if ((searchAddressText && searchAddressText.length > 3)) {
        let queryString = `q=${searchAddressText}`;
        let countryCode = OfficeDetails.countryId === 152 ? 'NZ' : 'AU'; 
       
        if (OfficeDetails.latitude && OfficeDetails.longitude) {
          queryString = `${queryString}&proximity=${OfficeDetails.longitude},${OfficeDetails.latitude}&country=${countryCode}&limit=5`;
        } else {
          queryString = `${queryString}&country=${countryCode}&limit=5`;
        }
   
        const addressResults = await qaddressSearch(queryString);
         if (addressResults.data && addressResults.data.length > 0) {
          setSearchAddressResults(addressResults.data);
        } else {
          setSearchAddressResults([]);
        }
      }
      setIsSearchingAddress(false);
    }, 500);

  const handleSubmit = async (values) => {
    setIsSavingListing(true);

    try {
      let auctionDateTime = '';
      if (values.auctionDate && values.auctionTime) {
        const formattedAuctionDate = values.auctionDate ? dayjs(values.auctionDate).format('YYYY-MM-DD') : '';
        auctionDateTime = `${formattedAuctionDate} ${values.auctionTime}`;
      }

      // Format dates for submission
      const formattedValues = {
        ...values,
        auctionDateTime,
        listingDate: values.listingDate ? dayjs(values.listingDate).format('YYYY-MM-DD') : '',
        expiryDate: values.expiryDate ? dayjs(values.expiryDate).format('YYYY-MM-DD') : '',
        agreementDate: values.agreementDate ? dayjs(values.agreementDate).format('YYYY-MM-DD') : '',
        leaseStartDate: values.leaseStartDate ? dayjs(values.leaseStartDate).format('YYYY-MM-DD') : '',
        leaseEndDate: values.leaseEndDate ? dayjs(values.leaseEndDate).format('YYYY-MM-DD') : '',
        availableDate: values.availableDate ? dayjs(values.availableDate).format('YYYY-MM-DD') : '',
        availableDateEnd: values.availableDateEnd ? dayjs(values.availableDateEnd).format('YYYY-MM-DD') : '',
      };

      const editListingJSONbody = {
        ...formattedValues,
        auctionDateTime
      };
      
      const resultsEditListing = await editListing(editListingJSONbody);

      if (resultsEditListing.status === 200 || resultsEditListing.status === 201) {
        await fetchAllData();
      }
      setIsSavingListing(false);
    } catch (error) {
      setIsSavingListing(false);
    }
  };

  if (isLoading) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="50vh"
        flexDirection="column"
      >
        <CircularProgress />
        <Typography variant="body2" sx={{ mt: 2 }}>
          Loading listing...
        </Typography>
      </Box>
    );
  }


  return (
      <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
        <Formik
          enableReinitialize
          initialValues={{
            propertyid: listing?.propertyId || '',
            userid: listing?.salesPersonId || '',
            userIdSp2: listing?.salesPersonId2 || '',
            streetNumber: listing?.streetNumber || '',
            streetName: listing?.streetName || '',
            regionid: listing?.regionId || '',
            Suburbid: listing?.suburbId || '',
            Stateid: listing?.stateId || '',
            postcode: listing?.postCode || '',
            priceFrom: String(listing?.priceFrom) || '',
            priceTo: String(listing?.priceTo) || '',
            propertySaleTypeid: String(listing?.propertySaleTypeID) || '',
            auctionDate: listing?.auctiondate ? dayjs(listing.auctiondate) : null,
            auctionTime: listing?.auctiontime || '',
            auctionDetails: listing?.auctionDetails || '',
            propertyStatusid: String(listing?.propertyStatusId) || '',
            rentalPrice: String(listing?.priceFrom) || '',
            propertyType: listing?.propertyType || '',
            propertyTypeid: listing?.propertyTypeId || '',
            listingDate: listing?.listingDate ? dayjs(listing.listingDate) : dayjs(),
            expiryDate: listing?.expiryDate ? dayjs(listing.expiryDate) : null,
            agreementDate: listing?.agreementDate ? dayjs(listing.agreementDate) : null,
            /*Res */
            bedrooms: String(listing?.bedrooms) || '',
            bathrooms: String(listing?.bathrooms) || '',
            carspaces: String(listing?.carSpaces) || '',
            garages: String(listing?.garages) || '',
            carports: String(listing?.carPorts) || '',
            landsizeunit: listing?.landSizeUnit || '',
            landsize: listing?.landSize || '',
            buildingArea: String(listing?.buildingArea) || '',
            airConditioning: String(listing?.airConditioning) || '',
            study: String(listing?.study) || '',
            pool: String(listing?.pool) || '',
            councilRates: listing?.CouncilRates || '',
            zoneCode: listing?.zoneCode || '',
            landDepth: listing?.landDepth || '',
            landWidth: listing?.landWidth || '',
            waterRates: listing?.waterRates || '',
            strataLevy: listing?.levies || '',
            energyRating: listing?.energyRating || '',
            newBuild: listing?.newBuild || '',
            /*Com */
            trussHeight: listing?.trussHeight || '',
            hardStand: listing?.hardStand || '',
            insurance: listing?.insurance || '',
            landTax: listing?.landTax || '',
            /*Rental */
            maxsleep: listing?.maxsleep || '',
            furnished: listing?.furnished || '',
            pets: listing?.pets || '',
            availableDate: listing?.availableDate ? dayjs(listing.availableDate) : null,
            availableDateEnd: listing?.availableDateEnd ? dayjs(listing.availableDateEnd) : null,
            bond: listing?.bond || '',
            rateTypeId: listing?.rateTypeId || '',
            leaseStartDate: listing?.leaseStartDate ? dayjs(listing.leaseStartDate) : null,
            leaseEndDate: listing?.leaseEndDate ? dayjs(listing.leaseEndDate) : null,
            leasePrice: listing?.leasePrice || '',
            /*Business*/
            internalOfficeCode: listing?.internalOfficeCode || '',
          }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ handleChange, handleBlur, handleSubmit, values, errors, touched, setFieldValue }) => (
            <form onSubmit={handleSubmit}>
              <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={'en-gb'}>
              <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
                {/* Website Link */}
                {/* Config.CRMWebsite && listing?.propertyId && (
                  <Box sx={{ textAlign: 'right', mb: 2 }}>
                    <Link
                      href={`${Config.CRMWebsite}/index.cfm?action=listings.main&propertyid=${listing.propertyId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      color="primary"
                    >
                      View this listing on website
                    </Link>
                  </Box>
                ) */}

                {/* Address Section */}
                <Typography variant="h6" gutterBottom>
                  Address
                </Typography>
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      label="Street No"
                      value={values.streetNumber}
                      onChange={handleChange('streetNumber')}
                      onBlur={handleBlur('streetNumber')}
                      type="number"
                      error={touched.streetNumber && !!errors.streetNumber}
                      helperText={touched.streetNumber && errors.streetNumber}
                    />
                  </Grid>
                  <Grid item xs={12} md={9}>
                    <TextField
                      fullWidth
                      label="Street Name"
                      value={values.streetName}
                      onChange={handleChange('streetName')}
                      onBlur={handleBlur('streetName')}
                      error={touched.streetName && !!errors.streetName}
                      helperText={touched.streetName && errors.streetName}
                    />
                  </Grid>
                </Grid>

                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={12} md={4}>
                    <Autocomplete
                      options={states}
                      getOptionLabel={(option) => option.state || ''}
                      value={states.find(state => state.stateId === values.Stateid) || null}
                      onChange={(event, newValue) => {
                        setFieldValue('Stateid', newValue?.stateId || '');
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="States"
                          error={touched.Stateid && !!errors.Stateid}
                          helperText={touched.Stateid && errors.Stateid}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Autocomplete
                      options={suburbs}
                      getOptionLabel={(option) => option.suburbLabel || ''}
                      value={suburbs.find(suburb => suburb.suburbid === values.Suburbid) || null}
                      onChange={(event, newValue) => {
                        setFieldValue('Suburbid', newValue?.suburbid || '');
                        setFieldValue('postcode', newValue?.postcode || '');
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Suburbs"
                          error={touched.Suburbid && !!errors.Suburbid}
                          helperText={touched.Suburbid && errors.Suburbid}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Autocomplete
                      options={Regions}
                      getOptionLabel={(option) => option.regionName || ''}
                      value={Regions.find(region => region.regionid === values.regionid) || null}
                      onChange={(event, newValue) => {
                        setFieldValue('regionid', newValue?.regionid || '');
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Regions"
                          error={touched.regionid && !!errors.regionid}
                          helperText={touched.regionid && errors.regionid}
                        />
                      )}
                    />
                  </Grid>
                </Grid>

                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Property Type"
                      value={listing?.propertyTypeTitle || ''}
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Autocomplete
                      options={propertySubTypes}
                      getOptionLabel={(option) => option.name || ''}
                      value={propertySubTypes.find(type => type.PropertyTypeID === values.propertyTypeid) || null}
                      onChange={(event, newValue) => {
                        setFieldValue('propertyTypeid', newValue?.PropertyTypeID || '');
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Property Sub-Type"
                          placeholder={listing.propertyTypeName === " " ? listing.propertyTypeName : 'Please Select PropertyType'}
                          error={touched.propertyTypeid && !!errors.propertyTypeid}
                          helperText={touched.propertyTypeid && errors.propertyTypeid}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </Paper>

              <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
                {/* Sale Type and Status */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={12} md={6}>
                    {!((values.propertyType === 'CL' || values.propertyType === 'H' || values.propertyType === 'L') || values.propertySaleTypeid === '4') && (
                      <FormControl fullWidth>
                        <InputLabel>Sale Type</InputLabel>
                        <Select
                          value={values.propertySaleTypeid}
                          onChange={(e) => {
                            setFieldValue('propertySaleTypeid', e.target.value);
                            if (e.target.value !== '2' && e.target.value !== '3') {
                              setFieldValue('auctionDate', '');
                              setFieldValue('auctionTime', '');
                              setFieldValue('auctionDetails', '');
                            }
                            if (e.target.value !== '4') {
                              setFieldValue('rateTypeId', '');
                            }
                          }}
                          label="Sale Type"
                        >
                          {((values.propertyType === 'CL' || values.propertyType === 'H' || values.propertyType === 'L') || values.propertySaleTypeid === '4') 
                            ? rentalType.map(type => (
                                <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>
                              ))
                            : SaleType.map(type => (
                                <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>
                              ))
                          }
                        </Select>
                      </FormControl>
                    )}
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Listing Status</InputLabel>
                      <Select
                        value={values.propertyStatusid}
                        onChange={(e) => setFieldValue('propertyStatusid', e.target.value)}
                        label="Listing Status"
                      >
                        {(listing.propertyType === 'CL' || listing.propertyType === 'H' || listing.propertyType === 'L' 
                          ? rentalListingStatus 
                          : saleListingStatus
                        ).map(status => (
                          <MenuItem key={status.value} value={status.value}>{status.label}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>

                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={12} md={6}>
                    <Autocomplete
                      options={Salesperson}
                      getOptionLabel={(option) => option.salespersonName || ''}
                      value={Salesperson.find(person => person.userid === values.userid) || null}
                      onChange={(event, newValue) => {
                        setFieldValue('userid', newValue?.userid || '');
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={values.propertySaleTypeid === '4' ? 'Property Manager' : 'Lister'}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Autocomplete
                      options={Salesperson}
                      getOptionLabel={(option) => option.salespersonName || ''}
                      value={Salesperson.find(person => person.userid === values.userIdSp2) || null}
                      onChange={(event, newValue) => {
                        setFieldValue('userIdSp2', newValue?.userid || '');
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={values.propertySaleTypeid === '4' ? 'Secondary Property Manager' : 'Secondary Lister'}
                        />
                      )}
                    />
                  </Grid>
                </Grid>

                {/* Lease Information for Leased Properties */}
                {values.propertyStatusid === '6' && (listing.propertyType === 'CL' || listing.propertyType === 'H' || listing.propertyType === 'L') && (
                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={12} md={4}>
                      <DatePicker
                        label="Date Lease Start"
                        value={values.leaseStartDate}
                        onChange={(newValue) => {
                          setFieldValue('leaseStartDate', newValue);
                        }}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            variant: 'outlined'
                          }
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <DatePicker
                        label="Date Lease End"
                        value={values.leaseEndDate}
                        onChange={(newValue) => {
                          setFieldValue('leaseEndDate', newValue);
                        }}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            variant: 'outlined'
                          }
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Lease Price ($ per week)"
                        value={values.leasePrice}
                        onChange={handleChange('leasePrice')}
                        onBlur={handleBlur('leasePrice')}
                        type="number"
                        slotProps={{
                          input: {
                            startAdornment: <InputAdornment position="start">$</InputAdornment>,
                          }
                        }}
                      />
                    </Grid>
                  </Grid>
                )}

                {/* Dates Section */}
                <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                  Dates
                </Typography>
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={12} md={4}>
                    
                        <DatePicker
                          label="Listing Date"
                          value={values.listingDate}
                          onChange={(newValue) => {
                            setFieldValue('listingDate', newValue);
                          }}
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              variant: 'outlined',
                              error: touched.listingDate && Boolean(errors.listingDate),
                              helperText: touched.listingDate && errors.listingDate
                            }
                          }}
                          
                        />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <DatePicker
                      label="Expiry Date"
                      value={values.expiryDate}
                      onChange={(newValue) => {
                        setFieldValue('expiryDate', newValue);
                      }}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          variant: 'outlined'
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <DatePicker
                      label="Office Agreement Start Date"
                      value={values.agreementDate}
                      onChange={(newValue) => {
                        setFieldValue('agreementDate', newValue);
                      }}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          variant: 'outlined'
                        }
                      }}
                    />
                  </Grid>
                </Grid>

                {/* Auction/Tender Information */}
                {(values.propertySaleTypeid === '2' || values.propertySaleTypeid === '3') && (
                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={12} md={4}>
                      <DatePicker
                        label={values.propertySaleTypeid === '2' ? 'Auction Date' : 'Tender Date'}
                        value={values.auctionDate}
                        onChange={(newValue) => {
                          setFieldValue('auctionDate', newValue);
                        }}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            variant: 'outlined'
                          }
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TimePicker
                        label={values.propertySaleTypeid === '2' ? 'Auction Time' : 'Tender Time'}
                        value={values.auctionTime ? dayjs(parseTimeStringToDate(values.auctionTime)) : null}
                        onChange={(newValue) => {
                          if (newValue) {
                            const formattedTime = formatTime(newValue.toDate());
                            setFieldValue('auctionTime', formattedTime);
                          } else {
                            setFieldValue('auctionTime', '');
                          }
                        }}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            variant: 'outlined'
                          }
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label={values.propertySaleTypeid === '2' ? 'Auction Details' : 'Tender Details'}
                        value={values.auctionDetails}
                        onChange={handleChange('auctionDetails')}
                        onBlur={handleBlur('auctionDetails')}
                        multiline
                        rows={3}
                      />
                    </Grid>
                  </Grid>
                )}
              </Paper>

              <div className="formSection">
                            <Typography className="sectionTitle">Extra Details</Typography>
                            <Grid container spacing={2}>
                              {/* Price From */}
                              <Grid >
                                <TextField
                                  label="Price From"
                                  value={values.priceFrom}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    setFieldValue('priceFrom', value);
                                    if (values.propertySaleTypeid === '4') {
                                      setFieldValue('rentalPrice', value);
                                    }
                                  }}
                                  onBlur={handleBlur('priceFrom')}
                                  type="number"
                                  variant="outlined"
                                  slotProps={{
                                    input: {
                                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                    }
                                  }}
                                  fullWidth
                                />
                              </Grid>
              
                              {/* Price To */}
                              <Grid >
                                <TextField
                                  label="Price To"
                                  value={values.priceTo}
                                  onChange={handleChange('priceTo')}
                                  onBlur={handleBlur('priceTo')}
                                  type="number"
                                  variant="outlined"
                                  slotProps={{
                                    input: {
                                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                    },
                                  }}
                                  fullWidth
                                />
                              </Grid>
                            </Grid>
                            {(values.propertySaleTypeid === '4' && (values.propertyType === 'CL' || values.propertyType === 'H' || values.propertyType === 'L')) ? (
                              <>
                                <Autocomplete
                                  options={getRateTypeOptions(values.propertyType)}
                                  getOptionLabel={(option) => option.label}
                                  value={getRateTypeOptions(values.propertyType).find((option) => option.value === values.rateTypeId) || null}
                                  onChange={(event, newValue) => {
                                    setFieldValue('rateTypeId', newValue ? newValue.value : '');
                                  }}
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      label="Select Rate Type"
                                      placeholder="Select Rate Type"
                                      sx={{ mt: 1 }}
                                    />
                                  )}
                                  isOptionEqualToValue={(option, value) => option.value === value.value}
                                />
                              </>
                            ) : null}
              
                            {/*Residential*/}
              
                            {values.propertyType === 'R' ? (
                              <>
                                <Grid container spacing={2}>
                                  {/* Bedrooms */}
                                  <Grid >
                                    <TextField
                                      label="Bedrooms"
                                      value={values.bedrooms}
                                      onChange={(e) => setFieldValue('bedrooms', e.target.value)}
                                      type="number"
                                      variant="outlined"
                                      fullWidth
                                    />
                                  </Grid>
              
                                  {/* Bathrooms */}
                                  <Grid >
                                    <TextField
                                      label="Bathrooms"
                                      value={values.bathrooms}
                                      onChange={(e) => setFieldValue('bathrooms', e.target.value)}
                                      type="number"
                                      variant="outlined"
                                      fullWidth
                                    />
                                  </Grid>
              
                                  {/* Car Spaces */}
                                  <Grid >
                                    <TextField
                                      label="Car Spaces"
                                      value={values.carspaces}
                                      onChange={(e) => setFieldValue('carspaces', e.target.value)}
                                      type="number"
                                      variant="outlined"
                                      fullWidth
                                    />
                                  </Grid>
              
                                  {/* Carports */}
                                  <Grid >
                                    <TextField
                                      label="Carports"
                                      value={values.carports}
                                      onChange={(e) => setFieldValue('carports', e.target.value)}
                                      type="number"
                                      variant="outlined"
                                      fullWidth
                                    />
                                  </Grid>
              
                                  {/* Garages */}
                                  <Grid >
                                    <TextField
                                      label="Garages"
                                      value={values.garages}
                                      onChange={(e) => setFieldValue('garages', e.target.value)}
                                      type="number"
                                      variant="outlined"
                                      fullWidth
                                    />
                                  </Grid>
              
                                  {/* Swimming Pools */}
                                  <Grid >
                                    <TextField
                                      label="Swimming Pools"
                                      value={values.pool}
                                      onChange={(e) => setFieldValue('pool', e.target.value)}
                                      type="number"
                                      variant="outlined"
                                      fullWidth
                                    />
                                  </Grid>
              
                                  {/* Study */}
                                  <Grid >
                                    <TextField
                                      label="Study"
                                      value={values.study}
                                      onChange={(e) => setFieldValue('study', e.target.value)}
                                      type="number"
                                      variant="outlined"
                                      fullWidth
                                    />
                                  </Grid>
              
                                  {/* Floor Area */}
                                  <Grid >
                                    <TextField
                                      label="Floor Area (m²)"
                                      value={values.buildingArea}
                                      onChange={(e) => setFieldValue('buildingArea', e.target.value)}
                                      type="number"
                                      variant="outlined"
                                      slotProps={{
                                        input: {
                                          endAdornment: <InputAdornment position="end">m²</InputAdornment>,
                                        },
                                      }}
                                      fullWidth
                                    />
                                  </Grid>
              
                                  {/* Land Size */}
                                  <Grid >
                                    <TextField
                                      label="Land Size"
                                      value={values.landsize}
                                      onChange={(e) => setFieldValue('landsize', e.target.value)}
                                      type="number"
                                      variant="outlined"
                                      fullWidth
                                    />
                                  </Grid>
              
                                  {/* Land Size Unit */}
                                  <Grid >
                                    <Autocomplete
                                      options={landSizeUnits}
                                      getOptionLabel={(option) => option.label}
                                      value={landSizeUnits.find((unit) => unit.value === values.landsizeunit) || null}
                                      onChange={(event, newValue) => {
                                        setFieldValue('landsizeunit', newValue ? newValue.value : '');
                                      }}
                                      renderInput={(params) => (
                                        <TextField {...params} label="Unit" placeholder="Unit" variant="outlined" />
                                      )}
                                      isOptionEqualToValue={(option, value) => option.value === value.value}
                                    />
                                  </Grid>
              
                                  {/* Land Width */}
                                  <Grid >
                                    <TextField
                                      label="Land Width (m)"
                                      value={values.landWidth}
                                      onChange={(e) => setFieldValue('landWidth', e.target.value)}
                                      type="number"
                                      variant="outlined"
                                      slotProps={{
                                        input: {
                                          endAdornment: <InputAdornment position="end">m</InputAdornment>,
                                        },
                                      }}
                                      fullWidth
                                    />
                                  </Grid>
              
                                  {/* Land Depth */}
                                  <Grid >
                                    <TextField
                                      label="Land Depth (m)"
                                      value={values.landDepth}
                                      onChange={(e) => setFieldValue('landDepth', e.target.value)}
                                      type="number"
                                      variant="outlined"
                                      slotProps={{
                                        input: {
                                          endAdornment: <InputAdornment position="end">m</InputAdornment>,
                                        },
                                      }}
                                      fullWidth
                                    />
                                  </Grid>
              
                                  {/* Zoning */}
                                  <Grid >
                                    <TextField
                                      label="Zoning"
                                      value={values.zoneCode}
                                      onChange={(e) => setFieldValue('zoneCode', e.target.value)}
                                      slotProps={{ maxLength: 50 }}
                                      variant="outlined"
                                      fullWidth
                                    />
                                  </Grid>
              
                                  {/* Council Rates */}
                                  <Grid >
                                    <TextField
                                      label="Council Rates"
                                      value={values.councilRates}
                                      onChange={(e) => setFieldValue('councilRates', e.target.value)}
                                      type="number"
                                      variant="outlined"
                                      slotProps={{
                                        input: {
                                          startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                        },
                                      }}
                                      fullWidth
                                    />
                                  </Grid>
              
                                  {/* Water Rates */}
                                  <Grid >
                                    <TextField
                                      label="Water Rates"
                                      value={values.waterRates}
                                      onChange={(e) => setFieldValue('waterRates', e.target.value)}
                                      type="number"
                                      variant="outlined"
                                      slotProps={{
                                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                      }}
                                      fullWidth
                                    />
                                  </Grid>
              
                                  {/* Strata Levies */}
                                  <Grid >
                                    <TextField
                                      label="Strata Levies"
                                      value={values.strataLevy}
                                      onChange={(e) => setFieldValue('strataLevy', e.target.value)}
                                      type="number"
                                      variant="outlined"
                                      slotProps={{
                                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                      }}
                                      fullWidth
                                    />
                                  </Grid>
              
                                  {/* Energy Efficiency Rating */}
                                  <Grid >
                                    <TextField
                                      label="Energy Efficiency Rating"
                                      value={values.energyRating}
                                      onChange={(e) => setFieldValue('energyRating', e.target.value)}
                                      variant="outlined"
                                      fullWidth
                                    />
                                  </Grid>
              
                                  {/* New Build */}
                                  <Grid >
                                    <Typography variant="subtitle1" gutterBottom>
                                      New Build/Home
                                    </Typography>
                                    <Autocomplete
                                      options={[
                                        { label: 'N/A', value: '' },
                                        { label: 'Yes', value: '1' },
                                        { label: 'No', value: '0' },
                                      ]}
                                      getOptionLabel={(option) => option.label}
                                      value={
                                        [
                                          { label: 'N/A', value: '' },
                                          { label: 'Yes', value: '1' },
                                          { label: 'No', value: '0' },
                                        ].find((option) => option.value === values.newBuild) || null
                                      }
                                      onChange={(event, newValue) => {
                                        setFieldValue('newBuild', newValue ? newValue.value : '');
                                      }}
                                      renderInput={(params) => (
                                        <TextField {...params} placeholder="Select..." variant="outlined" />
                                      )}
                                      isOptionEqualToValue={(option, value) => option.value === value.value}
                                    />
                                  </Grid>
              
                                  {/* Air Conditioning */}
                                  <Grid >
                                    <Typography variant="subtitle1" gutterBottom>
                                      Air Conditioning
                                    </Typography>
                                    <Autocomplete
                                      options={[
                                        { label: 'Yes', value: '1' },
                                        { label: 'No', value: '0' },
                                      ]}
                                      getOptionLabel={(option) => option.label}
                                      value={
                                        [
                                          { label: 'Yes', value: '1' },
                                          { label: 'No', value: '0' },
                                        ].find((option) => option.value === values.airConditioning) || null
                                      }
                                      onChange={(event, newValue) => {
                                        setFieldValue('airConditioning', newValue ? newValue.value : '');
                                      }}
                                      renderInput={(params) => (
                                        <TextField {...params} placeholder="Select..." variant="outlined" />
                                      )}
                                      isOptionEqualToValue={(option, value) => option.value === value.value}
                                    />
                                  </Grid>
                                </Grid>
                              </>
                            ) : null}
              
                            {/*Commercial For Sale and Commerical For Lease*/}
                            {(values.propertyType === 'C' || values.propertyType === 'CL') ? (
                              <>
                                <Grid container spacing={2}>
                                  {/* Land Size */}
                                  <Grid >
                                    <TextField
                                      label="Land Size (m²)"
                                      value={values.landSize}
                                      onChange={(e) => setFieldValue('landSize', e.target.value)}
                                      type="number"
                                      variant="outlined"
                                      slotProps={{
                                        endAdornment: <InputAdornment position="end">m²</InputAdornment>,
                                      }}
                                      fullWidth
                                    />
                                  </Grid>
              
                                  {/* Building Area */}
                                  <Grid >
                                    <TextField
                                      label="Building Area (m²)"
                                      value={values.buildingArea}
                                      onChange={(e) => setFieldValue('buildingArea', e.target.value)}
                                      type="number"
                                      variant="outlined"
                                      slotProps={{
                                        endAdornment: <InputAdornment position="end">m²</InputAdornment>,
                                      }}
                                      fullWidth
                                    />
                                  </Grid>
              
                                  {/* Land Tax */}
                                  <Grid >
                                    <TextField
                                      label="Land Tax"
                                      value={values.landTax}
                                      onChange={(e) => setFieldValue('landTax', e.target.value)}
                                      type="number"
                                      variant="outlined"
                                      slotProps={{
                                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                      }}
                                      fullWidth
                                    />
                                  </Grid>
              
                                  {/* Insurance */}
                                  <Grid >
                                    <TextField
                                      label="Insurance"
                                      value={values.insurance}
                                      onChange={(e) => setFieldValue('insurance', e.target.value)}
                                      type="number"
                                      variant="outlined"
                                      slotProps={{
                                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                      }}
                                      fullWidth
                                    />
                                  </Grid>
              
                                  {/* Zoning */}
                                  <Grid >
                                    <TextField
                                      label="Zoning"
                                      value={values.zoneCode}
                                      onChange={(e) => setFieldValue('zoneCode', e.target.value)}
                                      variant="outlined"
                                      slotProps={{ maxLength: 50 }}
                                      fullWidth
                                    />
                                  </Grid>
              
                                  {/* Council Rates */}
                                  <Grid >
                                    <TextField
                                      label="Council Rates"
                                      value={values.councilRates}
                                      onChange={(e) => setFieldValue('councilRates', e.target.value)}
                                      type="number"
                                      variant="outlined"
                                      slotProps={{
                                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                      }}
                                      fullWidth
                                    />
                                  </Grid>
              
                                  {/* Water Rates */}
                                  <Grid >
                                    <TextField
                                      label="Water Rates"
                                      value={values.waterRates}
                                      onChange={(e) => setFieldValue('waterRates', e.target.value)}
                                      type="number"
                                      variant="outlined"
                                      slotProps={{
                                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                      }}
                                      fullWidth
                                    />
                                  </Grid>
              
                                  {/* Strata Levies */}
                                  <Grid >
                                    <TextField
                                      label="Strata Levies"
                                      value={values.strataLevy}
                                      onChange={(e) => setFieldValue('strataLevy', e.target.value)}
                                      type="number"
                                      variant="outlined"
                                      slotProps={{
                                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                      }}
                                      fullWidth
                                    />
                                  </Grid>
              
                                  {/* Hardstand Area */}
                                  <Grid >
                                    <TextField
                                      label="Hardstand Area (m²)"
                                      value={values.hardStand}
                                      onChange={(e) => setFieldValue('hardStand', e.target.value)}
                                      type="number"
                                      variant="outlined"
                                      slotProps={{
                                        endAdornment: <InputAdornment position="end">m²</InputAdornment>,
                                      }}
                                      fullWidth
                                    />
                                  </Grid>
              
                                  {/* Truss Height */}
                                  <Grid >
                                    <TextField
                                      label="Truss Height (m)"
                                      value={values.trussHeight}
                                      onChange={(e) => setFieldValue('trussHeight', e.target.value)}
                                      type="number"
                                      variant="outlined"
                                      slotProps={{
                                        endAdornment: <InputAdornment position="end">m</InputAdornment>,
                                      }}
                                      fullWidth
                                    />
                                  </Grid>
              
                                  {/* Energy Efficiency Rating */}
                                  <Grid >
                                    <TextField
                                      label="Energy Efficiency Rating"
                                      value={values.energyRating}
                                      onChange={(e) => setFieldValue('energyRating', e.target.value)}
                                      variant="outlined"
                                      fullWidth
                                    />
                                  </Grid>
                                </Grid>
                              </>
                            ) : null}
                            {/*Rental Holiday Homes and Res*/}
                            {(values.propertyType === 'H' || values.propertyType === 'L') && (
                              <>
                                {/* Row 1 */}
                                <Grid container spacing={2}>
                                  <Grid >
                                    <TextField
                                      label="Bedrooms"
                                      type="number"
                                      value={values.bedrooms}
                                      onChange={(e) => setFieldValue('bedrooms', e.target.value)}
                                      variant="outlined"
                                      fullWidth
                                    />
                                  </Grid>
              
                                  {values.propertyType === 'H' && (
                                    <Grid >
                                      <TextField
                                        label="Max Sleep"
                                        type="number"
                                        value={values.maxsleep}
                                        onChange={(e) => setFieldValue('maxsleep', e.target.value)}
                                        variant="outlined"
                                        fullWidth
                                      />
                                    </Grid>
                                  )}
                                </Grid>
              
                                {/* Row 2 */}
                                <Grid container spacing={2}>
                                  <Grid >
                                    <TextField
                                      label="Bathrooms"
                                      type="number"
                                      value={values.bathrooms}
                                      onChange={(e) => setFieldValue('bathrooms', e.target.value)}
                                      variant="outlined"
                                      fullWidth
                                    />
                                  </Grid>
              
                                  <Grid >
                                    <TextField
                                      label="Car Spaces"
                                      type="number"
                                      value={values.carspaces}
                                      onChange={(e) => setFieldValue('carspaces', e.target.value)}
                                      variant="outlined"
                                      fullWidth
                                    />
                                  </Grid>
                                </Grid>
              
                                {/* Row 3 */}
                                <Grid container spacing={2}>
                                  <Grid >
                                    <TextField
                                      label="Carports"
                                      type="number"
                                      value={values.carports}
                                      onChange={(e) => setFieldValue('carports', e.target.value)}
                                      variant="outlined"
                                      fullWidth
                                    />
                                  </Grid>
              
                                  <Grid >
                                    <TextField
                                      label="Garages"
                                      type="number"
                                      value={values.garages}
                                      onChange={(e) => setFieldValue('garages', e.target.value)}
                                      variant="outlined"
                                      fullWidth
                                    />
                                  </Grid>
                                </Grid>
              
                                {/* Row 4 */}
                                <Grid container spacing={2}>
                                  <Grid >
                                    <TextField
                                      label="Swimming Pools"
                                      type="number"
                                      value={values.pool}
                                      onChange={(e) => setFieldValue('pool', e.target.value)}
                                      variant="outlined"
                                      fullWidth
                                    />
                                  </Grid>
              
                                  <Grid >
                                    <TextField
                                      label="Study"
                                      type="number"
                                      value={values.study}
                                      onChange={(e) => setFieldValue('study', e.target.value)}
                                      variant="outlined"
                                      fullWidth
                                    />
                                  </Grid>
                                </Grid>
              
                                {/* Row 5 */}
                                <Grid container spacing={2}>
                                  <Grid >
                                    <DatePicker
                                      label="Date Available"
                                      value={values.availableDate}
                                      onChange={(newValue) => setFieldValue('availableDate', newValue)}
                                      slotProps={{
                                        textField: {
                                          variant: 'outlined',
                                          fullWidth: true
                                        }
                                      }}
                                    />
                                  </Grid>
              
                                  {values.propertyType === 'H' && (
                                    <Grid >
                                      <DatePicker
                                        label="Available Date End"
                                        value={values.availableDateEnd}
                                        onChange={(newValue) => setFieldValue('availableDateEnd', newValue)}
                                        slotProps={{
                                          textField: {
                                            variant: 'outlined',
                                            fullWidth: true
                                          }
                                        }}
                                      />
                                    </Grid>
                                  )}
                                </Grid>
              
                                {/* Row 6 */}
                                <Grid container spacing={2}>
                                  <Grid >
                                    <TextField
                                      label="Bond"
                                      type="number"
                                      value={values.bond}
                                      onChange={(e) => setFieldValue('bond', e.target.value)}
                                      variant="outlined"
                                      fullWidth
                                    />
                                  </Grid>
                                </Grid>
              
                                {/* Row 7 */}
                                <Grid container spacing={2}>
                                  <Grid >
                                    <Typography variant="subtitle1" gutterBottom>
                                      Air Conditioning
                                    </Typography>
                                    <Autocomplete
                                      options={[
                                        { label: 'Yes', value: '1' },
                                        { label: 'No', value: '0' }
                                      ]}
                                      getOptionLabel={(option) => option.label}
                                      value={
                                        [{ label: 'Yes', value: '1' }, { label: 'No', value: '0' }]
                                          .find((option) => option.value === values.airConditioning) || null
                                      }
                                      onChange={(event, newValue) =>
                                        setFieldValue('airConditioning', newValue ? newValue.value : '')
                                      }
                                      renderInput={(params) => (
                                        <TextField {...params} placeholder="Select..." variant="outlined" />
                                      )}
                                      isOptionEqualToValue={(option, value) =>
                                        option.value === value.value
                                      }
                                    />
                                  </Grid>
              
                                  <Grid >
                                    <Typography variant="subtitle1" gutterBottom>
                                      Pets
                                    </Typography>
                                    <Autocomplete
                                      options={[
                                        { label: 'Yes', value: '1' },
                                        { label: 'No', value: '0' }
                                      ]}
                                      getOptionLabel={(option) => option.label}
                                      value={
                                        [{ label: 'Yes', value: '1' }, { label: 'No', value: '0' }]
                                          .find((option) => option.value === values.pets) || null
                                      }
                                      onChange={(event, newValue) =>
                                        setFieldValue('pets', newValue ? newValue.value : '')
                                      }
                                      renderInput={(params) => (
                                        <TextField {...params} placeholder="Select..." variant="outlined" />
                                      )}
                                      isOptionEqualToValue={(option, value) =>
                                        option.value === value.value
                                      }
                                    />
                                  </Grid>
                                </Grid>
              
                                {/* Row 8 */}
                                <Grid container spacing={2}>
                                  <Grid >
                                    <Typography variant="subtitle1" gutterBottom>
                                      Furnished
                                    </Typography>
                                    <Autocomplete
                                      options={[
                                        { label: 'Yes', value: '1' },
                                        { label: 'No', value: '0' }
                                      ]}
                                      getOptionLabel={(option) => option.label}
                                      value={
                                        [{ label: 'Yes', value: '1' }, { label: 'No', value: '0' }]
                                          .find((option) => option.value === values.furnished) || null
                                      }
                                      onChange={(event, newValue) =>
                                        setFieldValue('furnished', newValue ? newValue.value : '')
                                      }
                                      renderInput={(params) => (
                                        <TextField {...params} placeholder="Select..." variant="outlined" />
                                      )}
                                      isOptionEqualToValue={(option, value) =>
                                        option.value === value.value
                                      }
                                    />
                                  </Grid>
                                </Grid>
                              </>
                            )}
                            {/* Rural */}
                            {values.propertyType === 'RU' ? (
                              <>
                                <Grid container spacing={2}>
                                  {/* Bedrooms */}
                                  <Grid >
                                    <TextField
                                      label="Bedrooms"
                                      value={values.bedrooms}
                                      onChange={(e) => setFieldValue('bedrooms', e.target.value)}
                                      type="number"
                                      variant="outlined"
                                      fullWidth
                                    />
                                  </Grid>
              
                                  {/* Bathrooms */}
                                  <Grid >
                                    <TextField
                                      label="Bathrooms"
                                      value={values.bathrooms}
                                      onChange={(e) => setFieldValue('bathrooms', e.target.value)}
                                      type="number"
                                      variant="outlined"
                                      fullWidth
                                    />
                                  </Grid>
              
                                  {/* Car Spaces */}
                                  <Grid >
                                    <TextField
                                      label="Car Spaces"
                                      value={values.carspaces}
                                      onChange={(e) => setFieldValue('carspaces', e.target.value)}
                                      type="number"
                                      variant="outlined"
                                      fullWidth
                                    />
                                  </Grid>
              
                                  {/* Carports */}
                                  <Grid >
                                    <TextField
                                      label="Carports"
                                      value={values.carports}
                                      onChange={(e) => setFieldValue('carports', e.target.value)}
                                      type="number"
                                      variant="outlined"
                                      fullWidth
                                    />
                                  </Grid>
              
                                  {/* Garages */}
                                  <Grid >
                                    <TextField
                                      label="Garages"
                                      value={values.garages}
                                      onChange={(e) => setFieldValue('garages', e.target.value)}
                                      type="number"
                                      variant="outlined"
                                      fullWidth
                                    />
                                  </Grid>
              
                                  {/* Swimming Pools */}
                                  <Grid >
                                    <TextField
                                      label="Swimming Pools"
                                      value={values.pool}
                                      onChange={(e) => setFieldValue('pool', e.target.value)}
                                      type="number"
                                      variant="outlined"
                                      fullWidth
                                    />
                                  </Grid>
              
                                  {/* Study */}
                                  <Grid >
                                    <TextField
                                      label="Study"
                                      value={values.study}
                                      onChange={(e) => setFieldValue('study', e.target.value)}
                                      type="number"
                                      variant="outlined"
                                      slotProps={{ maxLength: 2 }}
                                      fullWidth
                                    />
                                  </Grid>
              
                                  {/* Energy Efficiency Rating */}
                                  <Grid >
                                    <TextField
                                      label="Energy Efficiency Rating"
                                      value={values.energyRating}
                                      onChange={(e) => setFieldValue('energyRating', e.target.value)}
                                      variant="outlined"
                                      fullWidth
                                    />
                                  </Grid>
              
                                  {/* Zoning */}
                                  <Grid >
                                    <TextField
                                      label="Zoning"
                                      value={values.zoneCode}
                                      onChange={(e) => setFieldValue('zoneCode', e.target.value)}
                                      variant="outlined"
                                      slotProps={{ maxLength: 50 }}
                                      fullWidth
                                    />
                                  </Grid>
              
                                  {/* Council Rates */}
                                  <Grid >
                                    <TextField
                                      label="Council Rates"
                                      value={values.councilRates}
                                      onChange={(e) => setFieldValue('councilRates', e.target.value)}
                                      type="number"
                                      variant="outlined"
                                      slotProps={{
                                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                      }}
                                      fullWidth
                                    />
                                  </Grid>
              
                                  {/* Water Rates */}
                                  <Grid >
                                    <TextField
                                      label="Water Rates"
                                      value={values.waterRates}
                                      onChange={(e) => setFieldValue('waterRates', e.target.value)}
                                      type="number"
                                      variant="outlined"
                                      slotProps={{
                                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                      }}
                                      fullWidth
                                    />
                                  </Grid>
              
                                  {/* Strata Levies */}
                                  <Grid >
                                    <TextField
                                      label="Strata Levies"
                                      value={values.strataLevy}
                                      onChange={(e) => setFieldValue('strataLevy', e.target.value)}
                                      type="number"
                                      variant="outlined"
                                      slotProps={{
                                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                      }}
                                      fullWidth
                                    />
                                  </Grid>
              
                                  {/* Land Size */}
                                  <Grid >
                                    <TextField
                                      label="Land Size"
                                      value={values.landSize}
                                      onChange={(e) => setFieldValue('landSize', e.target.value)}
                                      type="number"
                                      variant="outlined"
                                      fullWidth
                                    />
              
                                    {/* Land Size Unit */}
                                    <Autocomplete
                                      options={landSizeUnits}
                                      getOptionLabel={(option) => option.label}
                                      value={landSizeUnits.find((unit) => unit.value === values.landsizeunit) || null}
                                      onChange={(event, newValue) => {
                                        setFieldValue('landsizeunit', newValue ? newValue.value : '');
                                      }}
                                      renderInput={(params) => (
                                        <TextField
                                          {...params}
                                          label="Unit"
                                          placeholder="Unit"
                                          variant="outlined"
                                          sx={{ mt: 2 }}
                                        />
                                      )}
                                      isOptionEqualToValue={(option, value) => option.value === value.value}
                                    />
                                  </Grid>
                                </Grid>
                              </>
                            ) : null}
              
                            {/*Buiness*/}
                            {values.propertyType === 'B' ? (
                              <>
                                <Grid container spacing={2}>
                                  <Grid >
                                    <TextField
                                      label="Internal Office Code"
                                      value={values.internalOfficeCode}
                                      onChange={(e) => setFieldValue('internalOfficeCode', e.target.value)}
                                      variant="outlined"
                                      fullWidth
                                    />
                                  </Grid>
              
                                  <Grid >
                                    <TextField
                                      label="Strata Levies"
                                      value={values.strataLevy}
                                      onChange={(e) => setFieldValue('strataLevy', e.target.value)}
                                      type="number"
                                      variant="outlined"
                                      slotProps={{
                                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                      }}
                                      fullWidth
                                    />
                                  </Grid>
              
                                  <Grid >
                                    <TextField
                                      label="Energy Efficiency Rating"
                                      value={values.energyRating}
                                      onChange={(e) => setFieldValue('energyRating', e.target.value)}
                                      variant="outlined"
                                      fullWidth
                                    />
                                  </Grid>
                                </Grid>
              
              
                              </>
                            ) : null}
                          </div>

              {/* Submit Button */}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={isSavingListing}
                  sx={{ minWidth: 200 }}
                >
                  {isSavingListing ? <CircularProgress size={24} /> : 'Save Listing'}
                </Button>
              </Box>
              </LocalizationProvider>
            </form>
          )}
        </Formik>
      </Box>
  );
};

export default ListingFormEdit;
